import { useContext } from "react";
import {
    useCategoryStore,
    useCompanyStore,
    useDisclosureStore,
    useTabStore,
    useTopicStore
} from "~/stores";

import { AdvancedSearchContext, FilterType } from "~/providers/AdvancedSearchProvider";
import { SEARCH_TABS } from "~/types/search.types";
import { FilterOption, FilterType as DFilterType } from "~/types/filters.types";

interface IFilterType extends FilterType {
    categoryId: number;
    documentTypeId: number;
}

export const useSearchFn = () => {
    // Store states
    const disclosures = useDisclosureStore(state => state.disclosures);
    const activeTab = useTabStore(state => state.tab);

    // Store methods
    const getSelectedDisclosureIds = useCategoryStore(state => state.getSelectedDisclosureIds);
    const getSelectedTopics = useTopicStore(state => state.getSelectedTopics);
    const getSelectedCompanies = useCompanyStore(state => state.getSelectedCompanies);
    const activeDisclosure = useDisclosureStore(state => state.disclosure);
    const selectedCategoryIds = getSelectedDisclosureIds();

    // Advanced search methods
    const { setDisclosureFilters, setPageSectionFilters, setImageThemeFilters, setCompanyFilters } =
        useContext(AdvancedSearchContext);

    const updateSelectedDisclosures = () => {
        // Disclosures with selected Categories
        const disclosuresFilter = disclosures
            .filter(item => selectedCategoryIds.includes(item.exploreId))
            .map(item => ({
                id: item.exploreId,
                name: item?.label?.en || "",
                frName: item?.label?.fr || ""
            })) as FilterType[];
        // Set the Advanced search filters
        setDisclosureFilters(disclosuresFilter);
    };

    const updateSelectedTopics = () => {
        // Selected Topics
        const topicsFilter = getSelectedTopics().reduce((list: IFilterType[], topic) => {
            const { exploreId, categoryId, disclosureId, label } = topic;

            const topicItem: IFilterType = {
                id: exploreId,
                categoryId,
                documentTypeId: disclosureId,
                name: label?.en || "",
                frName: label?.fr || ""
            };

            return [...list, topicItem];
        }, []);

        switch (activeTab?.key) {
            case SEARCH_TABS.IMAGES: {
                setImageThemeFilters(topicsFilter);
                break;
            }

            case SEARCH_TABS.REPORTS: {
                setPageSectionFilters(topicsFilter);
                break;
            }

            case SEARCH_TABS.FULL_DOCUMENTS: {
                setDisclosureFilters([
                    {
                        id: activeDisclosure?.exploreId,
                        name: activeDisclosure?.label?.en || "",
                        frName: activeDisclosure?.label?.fr || ""
                    }
                ] as FilterType[]);
                const companies = getSelectedCompanies().map(item => {
                    const { id, name } = item;
                    return {
                        id,
                        label: name,
                        type: DFilterType.COMPANY
                    } as FilterOption;
                });
                setCompanyFilters(companies);
                break;
            }

            default:
                break;
        }
    };

    return {
        updateSelectedDisclosures,
        updateSelectedTopics
    };
};
